/**
 * Client-side validation for the forms the frontend owns.
 *
 * The server validates everything with Zod and is the authority; this exists only so a
 * person is told about a typo before a round trip.
 *
 * These functions answer with a *reason*, not a sentence. The module has no translator and no
 * React context, so any English it returned would be English on an Italian screen the first time
 * somebody rendered it — and a returned string is far too easy to render. A reason code cannot be
 * mistaken for copy: `routes/auth/validation.ts` and the settings panels map it to a catalogue
 * key, and the rule behind it stays written down exactly once, here.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export type FieldErrors<T extends string> = Partial<Record<T, string>>

export type EmailProblem = 'required' | 'malformed'
export type PasswordProblem = 'required' | 'tooShort' | 'tooLong' | 'needsMix'

export const validateEmail = (value: string): EmailProblem | undefined => {
  const trimmed = value.trim()
  if (!trimmed) return 'required'
  if (!EMAIL.test(trimmed)) return 'malformed'
  return undefined
}

export const validatePassword = (value: string): PasswordProblem | undefined => {
  if (!value) return 'required'
  if (value.length < 8) return 'tooShort'
  if (value.length > 200) return 'tooLong'
  return undefined
}

/** Stricter, for choosing a new password rather than typing an existing one. */
export const validateNewPassword = (value: string): PasswordProblem | undefined => {
  const basic = validatePassword(value)
  if (basic) return basic
  if (!/[a-zA-Z]/.test(value) || !/[0-9]/.test(value)) return 'needsMix'
  return undefined
}

/** Rough strength read, 0–3, shown as a bar under a new-password field. */
export const passwordStrength = (value: string): 0 | 1 | 2 | 3 => {
  if (value.length < 8) return 0
  let score = 0
  if (value.length >= 12) score += 1
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1
  if (/[0-9]/.test(value) && /[^a-zA-Z0-9]/.test(value)) score += 1
  return score === 0 ? 1 : score === 3 ? 3 : (score as 1 | 2)
}
